import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listProviderTemplates,
  type ProviderTemplate,
} from '../api/providerTemplates.js';
import { diffProviderTemplates } from './templateDrift.js';
import {
  createTemplate,
  type OutboundTemplate,
  type TemplateCategory,
  type TemplateStatus,
} from '../api/templates.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';

/**
 * #161 — Sync de templates desde el provider remoto al storage local.
 *
 * Lista los templates remotos (Meta Graph / 360dialog), los cruza con los
 * locales (matchea por templateName + language) y permite importar los
 * faltantes en bulk. Los que ya existen se muestran como tales.
 */

interface Props {
  tenantId: string;
  connectorId: string;
  localTemplates: OutboundTemplate[];
  onClose: () => void;
  onImported: () => void;
}

const CATEGORY_MAP: Record<string, TemplateCategory> = {
  MARKETING: 'MARKETING',
  UTILITY: 'UTILITY',
  AUTHENTICATION: 'AUTHENTICATION',
};

const STATUS_MAP: Record<string, TemplateStatus> = {
  APPROVED: 'approved',
  PENDING: 'pending',
  REJECTED: 'rejected',
};

function extractBody(components?: Array<Record<string, unknown>>): string {
  if (!components) return '';
  const body = components.find((c) => c['type'] === 'BODY');
  return typeof body?.['text'] === 'string' ? (body['text'] as string) : '';
}

function deriveLocalName(remote: ProviderTemplate): string {
  // El "name" canónico del provider suele ser snake_case; lo capitalizamos
  // para el display name local. El usuario puede editarlo luego.
  return remote.name
    .split('_')
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function SyncProviderTemplatesModal({
  tenantId,
  connectorId,
  localTemplates,
  onClose,
  onImported,
}: Props): JSX.Element {
  const pushToast = useUiStore((s) => s.pushToast);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const remoteQuery = useQuery({
    queryKey: ['provider-templates', tenantId, connectorId],
    queryFn: () => listProviderTemplates(tenantId, connectorId),
    enabled: Boolean(tenantId && connectorId),
  });

  const existing = useMemo(() => {
    const set = new Set<string>();
    for (const t of localTemplates) {
      if (t.connectorId === connectorId) {
        set.add(`${t.templateName}__${t.language}`);
      }
    }
    return set;
  }, [localTemplates, connectorId]);

  const remoteItems = remoteQuery.data ?? [];

  // Deriva: qué cambió del lado de Meta en las plantillas que ya teníamos.
  // Antes esto no se miraba y una recategorización (que cambia el precio por
  // mensaje) o un rechazo sobreviniente pasaban sin que nadie los viera.
  const drift = useMemo(
    () => diffProviderTemplates(localTemplates, remoteItems, connectorId),
    [localTemplates, remoteItems, connectorId],
  );
  const driftPorClave = useMemo(() => {
    const m = new Map<string, (typeof drift.cambiados)[number]>();
    for (const c of drift.cambiados) {
      m.set(`${c.local.templateName}__${c.local.language}`, c);
    }
    return m;
  }, [drift]);

  const importMut = useMutation({
    mutationFn: async () => {
      const toImport = remoteItems.filter((r) => selected.has(`${r.name}__${r.language}`));
      const results = await Promise.allSettled(
        toImport.map((r) =>
          createTemplate(tenantId, {
            connectorId,
            name: deriveLocalName(r),
            templateName: r.name,
            language: r.language,
            category: CATEGORY_MAP[r.category ?? 'UTILITY'] ?? 'UTILITY',
            status: STATUS_MAP[r.status] ?? 'pending',
            bodyText: extractBody(r.components),
            components: r.components,
            providerTemplateId: r.id,
          }),
        ),
      );
      const failed = results.filter((x) => x.status === 'rejected');
      return { ok: results.length - failed.length, failed: failed.length };
    },
    onSuccess: ({ ok, failed }) => {
      pushToast(
        failed === 0 ? 'success' : 'error',
        `Importados ${ok}${failed ? ` · fallaron ${failed}` : ''}`,
      );
      qc.invalidateQueries({ queryKey: ['templates', tenantId] });
      setSelected(new Set());
      onImported();
      if (failed === 0) onClose();
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const importableCount = remoteItems.filter(
    (r) => !existing.has(`${r.name}__${r.language}`),
  ).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-lg font-semibold">Sincronizar templates desde provider</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-3">
          {remoteQuery.isLoading && <p className="text-slate-500">Consultando provider…</p>}
          {remoteQuery.error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {(remoteQuery.error as Error).message}
            </div>
          )}
          {!remoteQuery.isLoading && !remoteQuery.error && remoteItems.length === 0 && (
            <p className="text-slate-500">El provider no devolvió templates.</p>
          )}
          {(drift.cambiados.length > 0 || drift.huerfanos.length > 0) && (
            <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
              <p className="font-semibold text-amber-900">
                Meta cambió {drift.cambiados.length} plantilla
                {drift.cambiados.length === 1 ? '' : 's'} que ya tenías
                {drift.huerfanos.length > 0
                  ? ` · ${drift.huerfanos.length} ya no existe${drift.huerfanos.length === 1 ? '' : 'n'} en el provider`
                  : ''}
              </p>
              <ul className="mt-2 space-y-1 text-amber-900">
                {drift.cambiados.map((c) => (
                  <li key={`${c.local.templateName}__${c.local.language}`}>
                    <span className="font-mono text-xs">{c.local.templateName}</span>{' '}
                    {c.cambios
                      .map((ch) => `${ch.campo}: ${ch.local} → ${ch.remoto}`)
                      .join(' · ')}
                    {c.rompeEnvio && (
                      <strong className="ml-2 text-red-700">no se puede enviar</strong>
                    )}
                    {c.afectaCosto && (
                      <strong className="ml-2 text-amber-800">cambia el costo por mensaje</strong>
                    )}
                    {c.desconocido && (
                      <span className="ml-2 text-slate-600">estado no reconocido</span>
                    )}
                  </li>
                ))}
                {drift.huerfanos.map((h) => (
                  <li key={h.id}>
                    <span className="font-mono text-xs">{h.templateName}</span> ya no está en el
                    provider: los envíos que la usen van a fallar
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-amber-800">
                El registro local no se actualiza solo. Revisá cada caso y editá la plantilla
                si corresponde.
              </p>
            </div>
          )}
          {remoteItems.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-2 py-2">Nombre</th>
                  <th className="px-2 py-2">Idioma</th>
                  <th className="px-2 py-2">Categoría</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2">Local</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {remoteItems.map((r) => {
                  const key = `${r.name}__${r.language}`;
                  const exists = existing.has(key);
                  return (
                    <tr key={key} className={exists ? 'opacity-60' : undefined}>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={!exists && selected.has(key)}
                          disabled={exists}
                          onChange={() => toggle(key)}
                          aria-label={`Importar ${r.name}`}
                        />
                      </td>
                      <td className="px-2 py-2 font-mono text-xs">{r.name}</td>
                      <td className="px-2 py-2 font-mono text-xs">{r.language}</td>
                      <td className="px-2 py-2 text-xs">{r.category ?? '—'}</td>
                      <td className="px-2 py-2 text-xs">{r.status}</td>
                      <td className="px-2 py-2 text-xs">
                        {exists ? (
                          driftPorClave.has(key) ? (
                            <span
                              className={
                                driftPorClave.get(key)!.rompeEnvio
                                  ? 'font-semibold text-red-700'
                                  : 'font-semibold text-amber-700'
                              }
                            >
                              ⚠ Cambió en Meta
                            </span>
                          ) : (
                            <span className="text-slate-500">✓ Sin cambios</span>
                          )
                        ) : (
                          <span className="text-amber-600">Nuevo</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-xs text-slate-500">
            {remoteItems.length} totales · {importableCount} importables · {selected.size}{' '}
            seleccionados
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => importMut.mutate()}
              disabled={selected.size === 0 || importMut.isPending}
              className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {importMut.isPending ? 'Importando…' : `Importar ${selected.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
